import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/services/adminApi";
import { formatChartNumber } from "@/lib/utils";
import { RefreshCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import type {
  EmbeddingRouterStats,
  GPUHealth,
  AccumulatorDetailedStats,
} from "@/types/admin";

const GPU_LABELS: Record<string, string> = {
  a10g: "A10G (Fast)",
  t4: "T4 (Medium)",
};

const GPU_COLORS: Record<string, string> = {
  a10g: "#3b82f6",
  t4: "#10b981",
};

const PRIORITY_LABELS: Record<string, string> = {
  P0: "P0 - Search",
  P1: "P1 - Recent",
  P2: "P2 - Backfill",
  P3: "P3 - Historical",
};

const PRIORITY_COLORS: Record<string, string> = {
  P0: "#ef4444",
  P1: "#3b82f6",
  P2: "#10b981",
  P3: "#f59e0b",
};

const REFRESH_INTERVAL_MS = 30_000;

export default function GPUMonitoringPage() {
  const [stats, setStats] = useState<EmbeddingRouterStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [gpuConfig, setGpuConfig] = useState<{
    useT4Gpu: boolean;
    forceP0Serverless: boolean;
    serverlessConfigured: boolean;
  } | null>(null);
  const [togglingT4, setTogglingT4] = useState(false);
  const [togglingP0Serverless, setTogglingP0Serverless] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const data = await adminApi.getEmbeddingStats();
      setStats(data);
      setError(null);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGpuConfig = useCallback(async () => {
    try {
      const cfg = await adminApi.getGpuConfig();
      setGpuConfig(cfg);
    } catch (err: any) {
      // Non-fatal: page still useful without the toggle
      console.warn("Failed to load GPU config", err);
    }
  }, []);

  const toggleT4 = useCallback(async () => {
    if (!gpuConfig || togglingT4) return;
    const next = !gpuConfig.useT4Gpu;
    setTogglingT4(true);
    try {
      const updated = await adminApi.updateGpuConfig({ useT4Gpu: next });
      setGpuConfig(updated);
      toast.success(
        updated.useT4Gpu
          ? "T4 GPU enabled — new P2/P3 traffic will route to T4"
          : "T4 GPU disabled — new P2/P3 traffic will spill to A10G; in-flight T4 batches will drain naturally",
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err.message || "Failed to update GPU config",
      );
    } finally {
      setTogglingT4(false);
    }
  }, [gpuConfig, togglingT4]);

  const toggleP0Serverless = useCallback(async () => {
    if (!gpuConfig || togglingP0Serverless) return;
    const next = !gpuConfig.forceP0Serverless;
    setTogglingP0Serverless(true);
    try {
      const updated = await adminApi.updateGpuConfig({ forceP0Serverless: next });
      setGpuConfig(updated);
      toast.success(
        updated.forceP0Serverless
          ? "Forced P0 → DeepInfra. All search queries now bypass the local P0 GPU."
          : "P0 routing restored to local P0 GPU (with DeepInfra slow-failover).",
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err.message || "Failed to update GPU config",
      );
    } finally {
      setTogglingP0Serverless(false);
    }
  }, [gpuConfig, togglingP0Serverless]);

  useEffect(() => {
    fetchStats();
    fetchGpuConfig();
  }, [fetchStats, fetchGpuConfig]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchStats, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStats]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load embedding stats: {error}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const gpuTiers = Object.keys(stats.health);
  const priorities = Object.keys(stats.totalRouted);
  const totalRequests = Object.values(stats.totalRouted).reduce(
    (a, b) => a + b,
    0
  );
  const totalInFlight = Object.values(stats.inFlight).reduce(
    (a, b) => a + b,
    0
  );
  const allHealthy = Object.values(stats.health).every((h) => h.healthy);
  const unhealthyCount = Object.values(stats.health).filter(
    (h) => !h.healthy
  ).length;

  // Chart data
  const routedChartData = priorities.map((p) => ({
    name: PRIORITY_LABELS[p] || p,
    value: stats.totalRouted[p] || 0,
    fill: PRIORITY_COLORS[p] || "#6b7280",
  }));

  const inFlightChartData = gpuTiers.map((tier) => ({
    name: GPU_LABELS[tier] || tier.toUpperCase(),
    inFlight: stats.inFlight[tier] || 0,
    fill: GPU_COLORS[tier] || "#6b7280",
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GPU Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">
            Embedding router health and performance
            {stats.legacyMode && (
              <Badge variant="warning" className="ml-2">
                Legacy Mode
              </Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-400">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto (30s)
          </label>
          <Button variant="outline" size="sm" onClick={fetchStats}>
            <RefreshCcw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard
          label="System Status"
          value={allHealthy ? "Healthy" : `${unhealthyCount} Down`}
          variant={allHealthy ? "success" : "error"}
        />
        <SummaryCard label="Total Routed" value={totalRequests.toLocaleString()} />
        <SummaryCard label="In-Flight" value={totalInFlight.toString()} />
        <SummaryCard
          label="Overflow"
          value={stats.overflowCount.toLocaleString()}
          subtitle={`${stats.overflowInFlight} t4→a10g, ${stats.a10gOverflowInFlight ?? 0} a10g→t4`}
        />
        <SummaryCard
          label="Failovers"
          value={stats.failoverCount.toLocaleString()}
          variant={stats.failoverCount > 0 ? "warning" : "default"}
        />
      </div>

      {/* GPU Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>GPU Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {gpuConfig === null ? (
            <p className="text-sm text-gray-500">Loading GPU configuration…</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">T4 GPU tier</span>
                    <Badge variant={gpuConfig.useT4Gpu ? "success" : "warning"}>
                      {gpuConfig.useT4Gpu ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    When disabled, P2/P3 traffic spills to A10G and in-flight T4 batches drain.
                    P0 search remains unaffected.
                  </p>
                </div>
                <Button
                  onClick={toggleT4}
                  disabled={togglingT4}
                  variant={gpuConfig.useT4Gpu ? "destructive" : "default"}
                  size="sm"
                >
                  {togglingT4 ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : gpuConfig.useT4Gpu ? (
                    "Disable T4"
                  ) : (
                    "Enable T4"
                  )}
                </Button>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      Force P0 → DeepInfra
                    </span>
                    <Badge variant={gpuConfig.forceP0Serverless ? "warning" : "default"}>
                      {gpuConfig.forceP0Serverless ? "Forced ON" : "Off"}
                    </Badge>
                    {!gpuConfig.serverlessConfigured && (
                      <Badge variant="error">DeepInfra unavailable</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    When ON, every P0 search query routes directly to DeepInfra and bypasses the
                    local P0 GPU entirely — even the slow-failover circuit. Use to drain the local
                    P0 endpoint or during an inference-server incident.
                    {!gpuConfig.serverlessConfigured &&
                      " DEEPINFRA_API_KEY is not configured on the server, so the toggle is disabled."}
                  </p>
                </div>
                <Button
                  onClick={toggleP0Serverless}
                  disabled={
                    togglingP0Serverless ||
                    (!gpuConfig.forceP0Serverless && !gpuConfig.serverlessConfigured)
                  }
                  variant={gpuConfig.forceP0Serverless ? "destructive" : "default"}
                  size="sm"
                >
                  {togglingP0Serverless ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : gpuConfig.forceP0Serverless ? (
                    "Stop forcing"
                  ) : (
                    "Force on"
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* GPU Health Cards */}
      <Card>
        <CardHeader>
          <CardTitle>GPU Health</CardTitle>
        </CardHeader>
        <CardContent>
          {gpuTiers.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No GPU tiers configured.{" "}
              {stats.legacyMode && "Running in legacy single-endpoint mode."}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {gpuTiers.map((tier) => (
                <GPUHealthCard
                  key={tier}
                  tier={tier}
                  health={stats.health[tier]}
                  inFlight={stats.inFlight[tier] || 0}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Routing by Priority */}
        <Card>
          <CardHeader>
            <CardTitle>Requests by Priority</CardTitle>
          </CardHeader>
          <CardContent>
            {totalRequests === 0 ? (
              <p className="text-gray-400 text-sm text-center py-8">
                No requests routed yet
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={routedChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, value }: { name?: string; value?: number }) =>
                      value && value > 0 ? `${name}: ${value.toLocaleString()}` : ""
                    }
                    labelLine={false}
                  >
                    {routedChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatChartNumber} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* In-Flight by GPU */}
        <Card>
          <CardHeader>
            <CardTitle>In-Flight Requests by GPU</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={inFlightChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="inFlight" name="In-Flight" radius={[4, 4, 0, 0]}>
                  {inFlightChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Batch Accumulator Stats */}
      {Object.keys(stats.accumulatorDetailedStats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Batch Accumulator Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GPU Tier</TableHead>
                  <TableHead className="text-right">Queue</TableHead>
                  <TableHead className="text-right">Batches</TableHead>
                  <TableHead className="text-right">Texts Processed</TableHead>
                  <TableHead className="text-right">Avg Batch Size</TableHead>
                  <TableHead className="text-right">Avg Wait</TableHead>
                  <TableHead className="text-right">Max Wait</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Deferred</TableHead>
                  <TableHead className="text-right">Starvation Flushes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(stats.accumulatorDetailedStats).map(
                  ([tier, detail]) => (
                    <AccumulatorRow
                      key={tier}
                      tier={tier}
                      detail={detail}
                      queueSize={stats.accumulatorStats[tier]?.queueSize || 0}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Routing Table */}
      <Card>
        <CardHeader>
          <CardTitle>Priority Routing Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Priority</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Primary GPU</TableHead>
                <TableHead className="text-right">Total Routed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <PriorityRow
                priority="P0"
                desc="Real-time search queries"
                gpu="A10G"
                count={stats.totalRouted["P0"] || 0}
              />
              <PriorityRow
                priority="P1"
                desc="Recent ingestion (<=30d)"
                gpu="A10G (batched)"
                count={stats.totalRouted["P1"] || 0}
              />
              <PriorityRow
                priority="P2"
                desc="Medium backfill (31-90d)"
                gpu="T4 (batched)"
                count={stats.totalRouted["P2"] || 0}
              />
              <PriorityRow
                priority="P3"
                desc="Historical backfill (>90d)"
                gpu="T4 (batched)"
                count={stats.totalRouted["P3"] || 0}
              />
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  subtitle,
  variant = "default",
}: {
  label: string;
  value: string;
  subtitle?: string;
  variant?: "default" | "success" | "warning" | "error";
}) {
  const borderColor = {
    default: "border-gray-200",
    success: "border-green-300",
    warning: "border-yellow-300",
    error: "border-red-300",
  }[variant];

  return (
    <div className={`bg-white rounded-lg border ${borderColor} p-4`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      )}
    </div>
  );
}

function GPUHealthCard({
  tier,
  health,
  inFlight,
}: {
  tier: string;
  health: GPUHealth;
  inFlight: number;
}) {
  const label = GPU_LABELS[tier] || tier.toUpperCase();
  const lastCheck = health.lastCheckAt
    ? new Date(health.lastCheckAt).toLocaleTimeString()
    : "Never";

  return (
    <div
      className={`rounded-lg border p-4 ${
        health.healthy
          ? "border-green-200 bg-green-50/50"
          : "border-red-200 bg-red-50/50"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-gray-900">{label}</span>
        <Badge variant={health.healthy ? "success" : "error"}>
          {health.healthy ? "Healthy" : "Unhealthy"}
        </Badge>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Latency</span>
          <span className="font-medium">
            {health.lastLatencyMs > 0
              ? `${health.lastLatencyMs.toFixed(0)}ms`
              : "--"}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">In-Flight</span>
          <span className="font-medium">{inFlight}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Consecutive Failures</span>
          <span
            className={`font-medium ${
              health.consecutiveFailures > 0
                ? "text-red-600"
                : "text-gray-900"
            }`}
          >
            {health.consecutiveFailures}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Last Check</span>
          <span className="text-gray-600">{lastCheck}</span>
        </div>
      </div>
    </div>
  );
}

function AccumulatorRow({
  tier,
  detail,
  queueSize,
}: {
  tier: string;
  detail: AccumulatorDetailedStats;
  queueSize: number;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {GPU_LABELS[tier] || tier.toUpperCase()}
      </TableCell>
      <TableCell className="text-right">{queueSize}</TableCell>
      <TableCell className="text-right">
        {detail.totalBatches.toLocaleString()}
      </TableCell>
      <TableCell className="text-right">
        {detail.totalTexts.toLocaleString()}
      </TableCell>
      <TableCell className="text-right">
        {detail.avgBatchSize.toFixed(1)}
      </TableCell>
      <TableCell className="text-right">
        {detail.avgWaitMs > 0 ? `${detail.avgWaitMs.toFixed(0)}ms` : "--"}
      </TableCell>
      <TableCell className="text-right">
        {detail.maxWaitMs > 0 ? `${detail.maxWaitMs.toFixed(0)}ms` : "--"}
      </TableCell>
      <TableCell className="text-right">
        <span className={detail.errors > 0 ? "text-red-600 font-medium" : ""}>
          {detail.errors}
        </span>
      </TableCell>
      <TableCell className="text-right">{detail.deferredFlushes}</TableCell>
      <TableCell className="text-right">{detail.maxWaitFlushes}</TableCell>
    </TableRow>
  );
}

function PriorityRow({
  priority,
  desc,
  gpu,
  count,
}: {
  priority: string;
  desc: string;
  gpu: string;
  count: number;
}) {
  return (
    <TableRow>
      <TableCell>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold"
          style={{
            backgroundColor:
              (PRIORITY_COLORS[priority] || "#6b7280") + "20",
            color: PRIORITY_COLORS[priority] || "#6b7280",
          }}
        >
          {priority}
        </span>
      </TableCell>
      <TableCell className="text-gray-600">{desc}</TableCell>
      <TableCell className="text-gray-600">{gpu}</TableCell>
      <TableCell className="text-right font-medium">
        {count.toLocaleString()}
      </TableCell>
    </TableRow>
  );
}
